import apiClient, { normalizeApiResponse, ApiResponse } from './apiClient';
import { validateToken } from './apiClient';

// Type definitions
export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest {
  name: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  user?: {
    _id: string;
    name: string;
    email: string;
    profilePicture: string;
    phoneNumber?: string;
    newUser?: boolean;
  };
  access_token?: string;
  refresh_token?: string;
  token?: string; // For backward compatibility
}

export interface User {
  _id: string;
  name: string;
  email: string;
  profilePicture: string;
  phoneNumber?: string;
  newUser?: boolean;
  // Below fields may come from other APIs
  subscriptionPlan?: string;
  subscriptionExpiry?: string;
  remainingMinutes?: number;
  activePlan?: string;
}

// Flag to track if user has manually logged out
let _userLoggedOut = false;

// Flag to track if logout is in progress
let _logoutInProgress = false;

// Flag to prevent multiple refresh token attempts for the same request
let _refreshTokenInProgress: Promise<AuthResponse> | null = null;

// Keep track of the current user profile fetch request
let _currentProfileFetch: Promise<User | null> | null = null;

// Cache the user profile to reduce API calls
let _cachedUserProfile: User | null = null;

// Track the last time the profile was fetched to enable periodic refreshes
let _lastProfileFetchTime = 0;

// Profile cache expiry time (5 minutes)
const PROFILE_CACHE_EXPIRY = 5 * 60 * 1000;

// Debounce utility to prevent multiple calls in quick succession
const debounce = (func: Function, wait: number) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function executedFunction(...args: any[]) {
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
};

// Debounced logout function to prevent multiple calls
const debouncedLogout = debounce((instance: AuthService, serverLogout: boolean) => {
  // Use the instance method to access the private method
  (instance as any)._logoutImplementation(serverLogout);
}, 300);

class AuthService {
  /**
   * User login
   * @param credentials User credentials
   * @returns Authentication response with user data and token
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    // Reset logout flag when attempting to login
    _userLoggedOut = false;
    _logoutInProgress = false;
    
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/auth/login', credentials);
    
    // Use the normalization utility for consistent response handling
    const normalizedResponse = normalizeApiResponse<AuthResponse>(response);
    
    // Extract data from normalized response
    const accessToken = normalizedResponse.access_token || normalizedResponse.token;
    const refreshToken = normalizedResponse.refresh_token;
    const user = normalizedResponse.user;
    
    if (accessToken) {
      apiClient.setToken(accessToken);
      // Also store the refresh token if available
      if (refreshToken) {
        localStorage.setItem('mocktrail-refresh-token', refreshToken);
      }
      
      // Cache the user profile from login response
      if (user) {
        _cachedUserProfile = user;
        _lastProfileFetchTime = Date.now();
      }
    }
    
    return normalizedResponse;
  }

  /**
   * User registration
   * @param userData User registration data
   * @returns Authentication response with user data and token
   */
  async signup(userData: SignupRequest): Promise<AuthResponse> {
    // Reset logout flag when attempting to signup
    _userLoggedOut = false;
    _logoutInProgress = false;
    
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/auth/signup', userData);
    
    // Use the normalization utility for consistent response handling
    const normalizedResponse = normalizeApiResponse<AuthResponse>(response);
    
    // Extract data from normalized response
    const accessToken = normalizedResponse.access_token || normalizedResponse.token;
    const refreshToken = normalizedResponse.refresh_token;
    const user = normalizedResponse.user;
    
    if (accessToken) {
      apiClient.setToken(accessToken);
      // Also store the refresh token if available
      if (refreshToken) {
        localStorage.setItem('mocktrail-refresh-token', refreshToken);
      }
      
      // Cache the user profile from signup response
      if (user) {
        _cachedUserProfile = user;
        _lastProfileFetchTime = Date.now();
      }
    }
    
    return normalizedResponse;
  }

  /**
   * Get current user profile
   * @param forceRefresh Force a fresh fetch even if cached data is available
   * @returns User profile data or null if not available
   */
  async getCurrentUser(forceRefresh = false): Promise<User | null> {
    // Create a unique request ID for this call for logging
    const requestId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    console.log(`AUTH SERVICE [${requestId}]: getCurrentUser called, forceRefresh=${forceRefresh}`);
    
    // Don't attempt to fetch if user has logged out
    if (_userLoggedOut || _logoutInProgress) {
      console.log(`AUTH SERVICE [${requestId}]: Skip getCurrentUser: User is logged out or logout in progress`);
      return null;
    }
    
    // Check for valid cached profile if not forcing refresh
    const now = Date.now();
    const isCacheValid = 
      !forceRefresh && 
      _cachedUserProfile && 
      (now - _lastProfileFetchTime < PROFILE_CACHE_EXPIRY);
    
    if (isCacheValid) {
      console.log(`AUTH SERVICE [${requestId}]: Using cached user profile from ${new Date(_lastProfileFetchTime).toISOString()}, age: ${(now - _lastProfileFetchTime)/1000}s`);
      return Promise.resolve(_cachedUserProfile);
    }
    
    // If there's already a request in progress and we're not forcing a refresh,
    // wait for that request instead of starting a new one
    if (_currentProfileFetch && !forceRefresh) {
      console.log(`AUTH SERVICE [${requestId}]: Using existing user profile fetch request that's already in progress`);
      return _currentProfileFetch;
    }
    
    console.log(`AUTH SERVICE [${requestId}]: Fetching fresh user profile`);
    
    // Verify authentication before proceeding
    if (!this.isAuthenticated()) {
      console.log(`AUTH SERVICE [${requestId}]: Skip getCurrentUser: User is not authenticated`);
      return null;
    }
    
    // Create a new profile fetch request
    _currentProfileFetch = (async () => {
      try {
        console.log(`AUTH SERVICE [${requestId}]: Making API call to /jobprot-api/users/me`);
        
        // Fetch user profile
        const response = await apiClient.get<ApiResponse<User>>('/jobprot-api/users/me');
        const userData = normalizeApiResponse<User>(response);
        
        console.log(`AUTH SERVICE [${requestId}]: Profile fetch successful`, {
          userId: userData?._id,
          hasProfilePicture: !!userData?.profilePicture
        });
        
        // Update cache with timestamp
        _cachedUserProfile = userData;
        _lastProfileFetchTime = Date.now();
        
        // Update mocktrail-auth-storage by directly updating the auth store
        try {
          // Access the auth store from window to avoid import cycles
          if (window && 'useAuthStore' in window) {
            console.log(`AUTH SERVICE [${requestId}]: Updating auth store with fresh user data`);
            const authStore = (window as any).useAuthStore;
            
            // Check if the getState function exists (typical Zustand pattern)
            if (authStore && typeof authStore.getState === 'function') {
              // Get the current state and updateUser function
              const state = authStore.getState();
              
              // If updateUser exists, call it to update the user in the store
              if (state && typeof state.updateUser === 'function') {
                state.updateUser(userData);
                console.log(`AUTH SERVICE [${requestId}]: Auth store updated with fresh user data`);
              } else {
                console.warn(`AUTH SERVICE [${requestId}]: Auth store updateUser function not found`);
              }
            } else {
              console.warn(`AUTH SERVICE [${requestId}]: Auth store getState function not found`);
            }
          } else {
            console.warn(`AUTH SERVICE [${requestId}]: Auth store not found on window object`);
          }
        } catch (storeError) {
          console.error(`AUTH SERVICE [${requestId}]: Error updating auth store:`, storeError);
        }
        
        return userData;
      } catch (error) {
        console.error(`AUTH SERVICE [${requestId}]: Error fetching user profile:`, error);
        
        // Handle 401 unauthorized - attempt to refresh token
        if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
          try {
            // Try to refresh the token
            console.log(`AUTH SERVICE [${requestId}]: Auth token expired, attempting to refresh`);
            await this.refreshToken();
            
            // Retry the request with the new token
            console.log(`AUTH SERVICE [${requestId}]: Token refreshed, retrying user profile fetch`);
            const response = await apiClient.get<ApiResponse<User>>('/jobprot-api/users/me');
            const userData = normalizeApiResponse<User>(response);
            
            // Update cache
            _cachedUserProfile = userData;
            _lastProfileFetchTime = Date.now();
            
            console.log(`AUTH SERVICE [${requestId}]: Profile fetch after token refresh successful`);
            
            // Update mocktrail-auth-storage by directly updating the auth store
            try {
              // Access the auth store from window to avoid import cycles
              if (window && 'useAuthStore' in window) {
                console.log(`AUTH SERVICE [${requestId}]: Updating auth store with fresh user data after token refresh`);
                const authStore = (window as any).useAuthStore;
                
                // Check if the getState function exists (typical Zustand pattern)
                if (authStore && typeof authStore.getState === 'function') {
                  // Get the current state and updateUser function
                  const state = authStore.getState();
                  
                  // If updateUser exists, call it to update the user in the store
                  if (state && typeof state.updateUser === 'function') {
                    state.updateUser(userData);
                    console.log(`AUTH SERVICE [${requestId}]: Auth store updated with fresh user data after token refresh`);
                  }
                }
              }
            } catch (storeError) {
              console.error(`AUTH SERVICE [${requestId}]: Error updating auth store after token refresh:`, storeError);
            }
            
            return userData;
          } catch (refreshError) {
            console.error(`AUTH SERVICE [${requestId}]: Failed to refresh token:`, refreshError);
            throw refreshError;
          }
        }
        
        throw error;
      } finally {
        // Clear the current fetch request
        _currentProfileFetch = null;
      }
    })();
    
    return _currentProfileFetch;
  }

  /**
   * Get cached user data without making API call
   * @returns User data or null if not available
   */
  getCachedUserData(): User | null {
    if (_cachedUserProfile) {
      return _cachedUserProfile;
    }
    
    return null;
  }

  /**
   * Set cached user data without making API call
   * @param userData User data to cache
   */
  setCachedUserData(userData: User): void {
    if (userData) {
      _cachedUserProfile = userData;
      _lastProfileFetchTime = Date.now();
    }
  }

  /**
   * Clear all user data (token and profile)
   * @param redirect Whether to redirect to login page
   */
  clearUserData(redirect = true): void {
    _userLoggedOut = true;
    _cachedUserProfile = null;
    _lastProfileFetchTime = 0;
    
    apiClient.clearToken();
    
    // Remove all authentication-related items from localStorage
    localStorage.removeItem('mocktrail-token');
    localStorage.removeItem('mocktrail-refresh-token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('mocktrail-auth-storage');
    
    // Also clear any cached user data
    localStorage.removeItem('cachedUserProfile');
    localStorage.removeItem('lastProfileFetchTime');
    
    console.log("AUTH SERVICE: All user data cleared from localStorage");
    
    if (redirect) {
      // Use replace state to prevent back navigation to protected routes
      window.location.replace('/login');
    }
  }

  /**
   * Internal implementation of logout that actually makes the API call
   * Should not be called directly - use logout() instead which debounces calls
   */
  private async _logoutImplementation(serverLogout = true): Promise<void> {
    // Set the flag to prevent multiple parallel logout attempts
    if (_logoutInProgress) {
      console.log("AUTH SERVICE: Logout already in progress, skipping duplicate call");
      return;
    }
    
    _logoutInProgress = true;
    _userLoggedOut = true; // Set this early to prevent new API calls during logout
    console.log("AUTH SERVICE: Logging out user (implementation)");
    
    // Store token in local variable to prevent reading from localStorage multiple times
    // This also ensures we use the same token throughout the logout process
    const accessToken = localStorage.getItem('mocktrail-token');
    const refreshToken = localStorage.getItem('mocktrail-refresh-token');
    
    console.log("AUTH SERVICE: Tokens retrieved for logout", {
      hasAccessToken: !!accessToken,
      accessTokenPrefix: accessToken ? accessToken.substring(0, 10) + '...' : 'none',
      hasRefreshToken: !!refreshToken,
      refreshTokenPrefix: refreshToken ? refreshToken.substring(0, 10) + '...' : 'none'
    });
    
    // First make the server logout API call while tokens are still available
    // then clear the tokens from storage after the call completes
    if (serverLogout && accessToken) {
      try {
        // Include the refresh token in the request headers
        const headers: Record<string, string> = { 
          'Authorization': `Bearer ${accessToken}`
        };
        
        if (refreshToken) {
          console.log("AUTH SERVICE: Calling server logout with refresh token");
          headers['X-Refresh-Token'] = refreshToken;
          
          // Make only one API call with both tokens
          console.log("AUTH SERVICE: Preparing to make logout API call");
          const response = await apiClient.post('/auth/logout', 
            { refresh_token: refreshToken }, 
            { headers }
          );
          console.log("AUTH SERVICE: Server logout API call completed successfully with status:", response);
        } else {
          // If no refresh token, still try to logout
          console.log("AUTH SERVICE: Calling server logout without refresh token");
          const response = await apiClient.post('/auth/logout', undefined, { headers });
          console.log("AUTH SERVICE: Server logout API call completed successfully with status:", response);
        }
      } catch (error) {
        console.error('AUTH SERVICE: Server logout failed', error);
        // Continue with client logout even if server logout fails
      }
    } else {
      console.log(`AUTH SERVICE: Skipping server logout API call, serverLogout: ${serverLogout}, hasAccessToken: ${!!accessToken}`);
    }
    
    // Now remove tokens from localStorage AFTER the API call
    console.log("AUTH SERVICE: Clearing tokens from localStorage");
    localStorage.removeItem('mocktrail-token');
    localStorage.removeItem('mocktrail-refresh-token');
    localStorage.removeItem('isAuthenticated');
    
    // Now complete the local logout process
    console.log("AUTH SERVICE: Completing local logout");
    
    // Clear user cache
    _cachedUserProfile = null;
    _lastProfileFetchTime = 0;
    
    // Clear token from apiClient
    apiClient.clearToken();
    
    // Remove all authentication-related items from localStorage (again as a safety net)
    localStorage.removeItem('mocktrail-token');
    localStorage.removeItem('mocktrail-refresh-token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('mocktrail-auth-storage');
    
    // Also clear any cached user data
    localStorage.removeItem('cachedUserProfile');
    localStorage.removeItem('lastProfileFetchTime');
    
    console.log("AUTH SERVICE: All user data cleared from localStorage");
    
    // Reset the flag
    _logoutInProgress = false;
    
    // Handle the redirect at the end to avoid race conditions
    if (serverLogout) {
      console.log("AUTH SERVICE: Redirecting to login page");
      window.location.replace('/login');
    }
  }

  /**
   * Logout user
   * @param serverLogout Whether to also logout on server
   */
  async logout(serverLogout = true): Promise<void> {
    console.log("AUTH SERVICE: Logout requested, server logout:", serverLogout);
    console.log("AUTH SERVICE: Token status before debounce:", {
      hasToken: !!localStorage.getItem('mocktrail-token'),
      hasRefreshToken: !!localStorage.getItem('mocktrail-refresh-token')
    });
    
    // Use the debounced implementation to prevent multiple calls in quick succession
    debouncedLogout(this, serverLogout);
  }

  /**
   * Check if user is authenticated
   * @returns Boolean indicating authentication status
   */
  isAuthenticated(): boolean {
    // If user has explicitly logged out or logout is in progress
    if (_userLoggedOut || _logoutInProgress) {
      return false;
    }
    
    const token = localStorage.getItem('mocktrail-token');
    return token !== null && validateToken(token);
  }

  /**
   * Refresh auth token
   * @returns New authentication response
   */
  async refreshToken(): Promise<AuthResponse> {
    // If user is logged out or logout is in progress, don't try to refresh
    if (_userLoggedOut || _logoutInProgress) {
      throw new Error('User is logged out');
    }
    
    // If there's already a refresh in progress, use that promise instead of starting a new one
    if (_refreshTokenInProgress) {
      console.log("AUTH SERVICE: Using existing refresh token request");
      return _refreshTokenInProgress;
    }
    
    console.log("AUTH SERVICE: Starting new refresh token request");
    
    // Create a new refresh token request
    _refreshTokenInProgress = (async () => {
      try {
        const refreshToken = localStorage.getItem('mocktrail-refresh-token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        console.log("AUTH SERVICE: Calling refresh token API");
        const response = await apiClient.post<ApiResponse<AuthResponse>>('/auth/refresh', { refresh_token: refreshToken });
        
        // Use the normalization utility for consistent response handling
        const normalizedResponse = normalizeApiResponse<AuthResponse>(response);
        
        // Extract data from normalized response
        const accessToken = normalizedResponse.access_token || normalizedResponse.token;
        const newRefreshToken = normalizedResponse.refresh_token;
        const user = normalizedResponse.user;
        
        if (accessToken) {
          console.log("AUTH SERVICE: Token refresh successful, updating token");
          apiClient.setToken(accessToken);
          
          // Update refresh token if provided
          if (newRefreshToken) {
            localStorage.setItem('mocktrail-refresh-token', newRefreshToken);
          }
          
          // Update cached user profile if present in response
          if (user) {
            _cachedUserProfile = user;
            _lastProfileFetchTime = Date.now();
          }
        } else {
          console.error("AUTH SERVICE: Refresh token response missing access_token");
        }
        
        return normalizedResponse;
      } catch (error) {
        console.error("AUTH SERVICE: Token refresh failed", error);
        
        // If refresh token is invalid/expired, clear the user data (but don't redirect yet)
        if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
          console.log("AUTH SERVICE: Refresh token invalid/expired, marking user as logged out");
          _userLoggedOut = true;
        }
        
        throw error;
      } finally {
        // Clear the refresh token promise regardless of success/failure
        _refreshTokenInProgress = null;
      }
    })();
    
    return _refreshTokenInProgress;
  }

  /**
   * Update user data
   * This method will update the user's data in the API and return the updated user
   * @param userData Partial user data to update
   * @returns Updated user data
   */
  async updateUser(userData: Partial<User>): Promise<User> {
    // Verify the user is authenticated before proceeding
    if (!this.isAuthenticated()) {
      console.error('Cannot update user data: Not authenticated');
      throw new Error('Not authenticated');
    }
    
    try {
      console.log('Updating user data:', userData);
      
      // Make the API call to update user data
      const response = await apiClient.put<ApiResponse<User>>('/jobprot-api/users/me', userData);
      
      // Use the normalization utility for consistent response handling
      const normalizedResponse = normalizeApiResponse<User>(response);
      
      // Update cached user profile
      if (normalizedResponse) {
        // Merge the new data with the cached user profile
        _cachedUserProfile = {
          ..._cachedUserProfile,
          ...normalizedResponse
        };
        _lastProfileFetchTime = Date.now();
        
        console.log('User data updated successfully:', _cachedUserProfile);
      }
      
      return normalizedResponse;
    } catch (error) {
      console.error('Error updating user data:', error);
      
      // If we get a 401 error, try to refresh token and retry
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        console.log('Authentication error while updating user data, attempting to refresh token');
        
        try {
          // Try to refresh the token
          await this.refreshToken();
          
          // If refresh is successful, retry the update
          console.log('Token refreshed, retrying user update');
          return this.updateUser(userData);
        } catch (refreshError) {
          console.error('Failed to refresh token during user update:', refreshError);
          throw refreshError;
        }
      }
      
      throw error;
    }
  }

  /**
   * Reset password with token
   * @param token Reset token
   * @param password New password
   */
  async resetPassword(token: string, password: string): Promise<void> {
    await apiClient.post('/auth/reset-password', { token, password });
  }
  
  /**
   * Request password reset
   * @param email User email
   */
  async requestPasswordReset(email: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { email });
  }
}

// Export service as singleton
export const authService = new AuthService();
export default authService; 